/**
 * Initiative Portfolio - React Query hooks. Academy pattern: a key factory
 * rooted at one namespace plus a blunt whole-module invalidator on mutation
 * settle (the register is small; precision invalidation isn't worth drift).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './portfolio';

export const portfolioKeys = {
  all: ['portfolio'] as const,
  meta: () => [...portfolioKeys.all, 'meta'] as const,
  initiatives: (includeArchived: boolean) =>
    [...portfolioKeys.all, 'initiatives', { includeArchived }] as const,
  initiative: (id: string) => [...portfolioKeys.all, 'initiative', id] as const,
  my: () => [...portfolioKeys.all, 'my'] as const,
  blockers: (includeResolved: boolean) =>
    [...portfolioKeys.all, 'blockers', { includeResolved }] as const,
  risks: (includeClosed: boolean, highOnly: boolean) =>
    [...portfolioKeys.all, 'risks', { includeClosed, highOnly }] as const,
  myWork: () => [...portfolioKeys.all, 'my-work'] as const,
  decisions: (includeHistory: boolean) =>
    [...portfolioKeys.all, 'decisions', { includeHistory }] as const,
  dashboard: () => [...portfolioKeys.all, 'dashboard'] as const,
  subscription: () => [...portfolioKeys.all, 'subscription'] as const,
};

function usePortfolioInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: portfolioKeys.all });
}

// ── Queries ──

export function usePortfolioMeta() {
  return useQuery({
    queryKey: portfolioKeys.meta(),
    queryFn: api.fetchMeta,
    staleTime: 5 * 60_000,
  });
}

export function useInitiatives(includeArchived: boolean) {
  return useQuery({
    queryKey: portfolioKeys.initiatives(includeArchived),
    queryFn: () => api.fetchInitiatives(includeArchived),
  });
}

export function useInitiative(id: string | undefined) {
  return useQuery({
    queryKey: portfolioKeys.initiative(id ?? ''),
    queryFn: () => api.fetchInitiative(id!),
    enabled: !!id,
  });
}

export function useMyInitiatives() {
  return useQuery({ queryKey: portfolioKeys.my(), queryFn: api.fetchMyInitiatives });
}

/** Every blocker across the register, newest raised first (the Blockers page). */
export function useBlockers(includeResolved: boolean) {
  return useQuery({
    queryKey: portfolioKeys.blockers(includeResolved),
    queryFn: () => api.fetchBlockers(includeResolved),
  });
}

export function usePortfolioDashboard() {
  return useQuery({ queryKey: portfolioKeys.dashboard(), queryFn: api.fetchDashboard });
}

// ── Mutations ──

export function useCreateInitiative() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.createInitiative, onSettled: invalidate });
}

export function useUpdateInitiative() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: api.InitiativePatch }) =>
      api.updateInitiative(id, patch),
    onSettled: invalidate,
  });
}

export function useSubmitSnapshot() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: api.SnapshotSubmitInput }) =>
      api.submitSnapshot(id, input),
    onSettled: invalidate,
  });
}

/**
 * "Add to VELT". Optimistic on the list cache: a checkbox that waits for a
 * round trip before ticking reads as broken, and the settle refetch corrects
 * an unlucky one (a withdrawal the server refuses, say).
 */
export function useSetVeltFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isVelt }: { id: string; isVelt: boolean }) => api.setVeltFlag(id, isVelt),
    onMutate: async ({ id, isVelt }) => {
      await qc.cancelQueries({ queryKey: portfolioKeys.all });
      qc.setQueriesData<api.Initiative[]>(
        { queryKey: [...portfolioKeys.all, 'initiatives'] },
        (rows) => rows?.map((r) => (r.id === id ? { ...r, isVelt } : r))
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: portfolioKeys.all }),
  });
}

export function useSetPriority() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ id, priorityId }: { id: string; priorityId: string | null }) =>
      api.setPriority(id, priorityId),
    onSettled: invalidate,
  });
}

export function useAddLink() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ initiativeId, input }: { initiativeId: string; input: api.LinkInput }) =>
      api.addLink(initiativeId, input),
    onSettled: invalidate,
  });
}

export function useUpdateLink() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ linkId, patch }: { linkId: string; patch: api.LinkPatch }) =>
      api.updateLink(linkId, patch),
    onSettled: invalidate,
  });
}

export function useDeleteLink() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.deleteLink, onSettled: invalidate });
}

export function useAddStakeholder() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({
      initiativeId,
      input,
    }: {
      initiativeId: string;
      input: api.StakeholderInput;
    }) => api.addStakeholder(initiativeId, input),
    onSettled: invalidate,
  });
}

export function useUpdateStakeholder() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({
      stakeholderId,
      patch,
    }: {
      stakeholderId: string;
      patch: api.StakeholderPatch;
    }) => api.updateStakeholder(stakeholderId, patch),
    onSettled: invalidate,
  });
}

export function useDeleteStakeholder() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.deleteStakeholder, onSettled: invalidate });
}

export function useDeleteSnapshot() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.deleteSnapshot, onSettled: invalidate });
}

export function useCreateBlocker() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ initiativeId, input }: { initiativeId: string; input: api.BlockerCreateInput }) =>
      api.createBlocker(initiativeId, input),
    onSettled: invalidate,
  });
}

export function useUpdateBlocker() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ blockerId, patch }: { blockerId: string; patch: api.BlockerPatch }) =>
      api.updateBlocker(blockerId, patch),
    onSettled: invalidate,
  });
}

// ── Decisions (ADO #1495) ──

export function useDecisions(includeHistory = false) {
  return useQuery({
    queryKey: portfolioKeys.decisions(includeHistory),
    queryFn: () => api.fetchDecisions(includeHistory),
  });
}

export function useCreateDecision() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.createDecision, onSettled: invalidate });
}

export function useUpdateDecision() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({
      decisionId,
      patch,
    }: {
      decisionId: string;
      patch: Partial<api.DecisionInput>;
    }) => api.updateDecision(decisionId, patch),
    onSettled: invalidate,
  });
}

export function useSupersedeDecision() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ decisionId, replacementId }: { decisionId: string; replacementId: string }) =>
      api.supersedeDecision(decisionId, replacementId),
    onSettled: invalidate,
  });
}

export function useDeleteDecision() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.deleteDecision, onSettled: invalidate });
}

export function useCreateImpactArea() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.createImpactArea, onSettled: invalidate });
}

// ── Risks and next steps (ADO #1551) ──

/**
 * Every risk across the register, highest exposure first (the Risks page).
 * `highOnly` is the executive read: the dashboard and any leadership surface
 * ask for that one, so a low-band risk logged by a team never reaches them.
 */
export function useRisks(opts: { includeClosed?: boolean; highOnly?: boolean } = {}) {
  const includeClosed = opts.includeClosed ?? false;
  const highOnly = opts.highOnly ?? false;
  return useQuery({
    queryKey: portfolioKeys.risks(includeClosed, highOnly),
    queryFn: () => api.fetchRisks({ includeClosed, highOnly }),
  });
}

export function useCreateRisk() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ initiativeId, input }: { initiativeId: string; input: api.RiskCreateInput }) =>
      api.createRisk(initiativeId, input),
    onSettled: invalidate,
  });
}

export function useUpdateRisk() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ riskId, patch }: { riskId: string; patch: api.RiskPatch }) =>
      api.updateRisk(riskId, patch),
    onSettled: invalidate,
  });
}

/** "This happened": closes the risk as occurred and raises the linked blocker. */
export function usePromoteRisk() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({
      riskId,
      input,
    }: {
      riskId: string;
      input: Parameters<typeof api.promoteRisk>[1];
    }) => api.promoteRisk(riskId, input),
    onSettled: invalidate,
  });
}

export function useDeleteRisk() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.deleteRisk, onSettled: invalidate });
}

export function useCreateAction() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ initiativeId, input }: { initiativeId: string; input: api.ActionCreateInput }) =>
      api.createAction(initiativeId, input),
    onSettled: invalidate,
  });
}

export function useUpdateAction() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ actionId, patch }: { actionId: string; patch: api.ActionPatch }) =>
      api.updateAction(actionId, patch),
    onSettled: invalidate,
  });
}

export function useDeleteAction() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.deleteAction, onSettled: invalidate });
}

/** Next steps, risks, and blockers waiting on the caller: the My Work page. */
export function useMyWork() {
  return useQuery({ queryKey: portfolioKeys.myWork(), queryFn: api.fetchMyWork });
}

export function useCreateBusinessUnit() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.createBusinessUnit, onSettled: invalidate });
}

export function useCreateStrategicPillar() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.createStrategicPillar, onSettled: invalidate });
}

export function useCreateProgram() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.createProgram, onSettled: invalidate });
}

export function useCreateLookup() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ kind, name }: { kind: api.LookupKind; name: string }) =>
      api.createLookup(kind, name),
    onSettled: invalidate,
  });
}

export function useUpdateLookup() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({
    mutationFn: ({ kind, id, patch }: { kind: api.LookupKind; id: string; patch: api.LookupPatch }) =>
      api.updateLookup(kind, id, patch),
    onSettled: invalidate,
  });
}

export function useSaveDisplayLabels() {
  const invalidate = usePortfolioInvalidator();
  return useMutation({ mutationFn: api.saveDisplayLabels, onSettled: invalidate });
}

// ── Report subscriptions (the digest + list emails) ──

export function useSubscriptions() {
  return useQuery({
    queryKey: portfolioKeys.subscription(),
    queryFn: api.fetchSubscriptions,
  });
}

export function useSaveSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ report, input }: { report: api.DigestReportKind; input: api.SubscriptionInput }) =>
      api.saveSubscription(report, input),
    onSettled: () => qc.invalidateQueries({ queryKey: portfolioKeys.subscription() }),
  });
}

export function useRemoveSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (report: api.DigestReportKind) => api.removeSubscription(report),
    onSettled: () => qc.invalidateQueries({ queryKey: portfolioKeys.subscription() }),
  });
}

export function useSendDigestNow() {
  return useMutation({
    mutationFn: ({ report, input }: { report: api.DigestReportKind; input?: api.SubscriptionInput }) =>
      api.sendDigestNow(report, input),
  });
}

// ── Per-user preferences ──

/**
 * A server-persisted per-user preference (portfolio.user_pref). Writes are
 * optimistic: the cache updates instantly and the PUT is fire-and-forget,
 * so filter clicks never feel slow. `loaded` distinguishes "showing the
 * default because nothing is saved" from "still fetching".
 */
export function usePortfolioPref<T>(key: string, defaultValue: T) {
  const qc = useQueryClient();
  const queryKey = [...portfolioKeys.all, 'pref', key] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => api.fetchPref<T>(key),
    staleTime: 5 * 60_000,
  });
  const save = useMutation({ mutationFn: (v: T) => api.savePref(key, v) });
  const value = (query.data ?? defaultValue) as T;
  const setValue = (v: T) => {
    qc.setQueryData(queryKey, v);
    save.mutate(v);
  };
  return { value, setValue, loaded: !query.isLoading };
}
